import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  CSSProperties,
} from 'react'
import ReactMarkdown from 'react-markdown'
import LoadingText from '../LoadingText'
import {
  textareaStyle,
  markdownContainerStyle,
} from '../styles/AIEditableText.styles'

const ANIMATION_INTERVAL = 25
const CHUNK_SIZE = 5

type AnimateType = 'NONE' | 'ANIMATE_NOW' | 'ANIMATE_ON_ENTER_SCREEN'

interface AIEditableTextProps {
  text: string
  isLoading?: boolean
  style?: CSSProperties
  animateType?: AnimateType
  isEditable?: boolean
  onAnimatingChange?: (isAnimating: boolean) => void
  onRef?: (ref: HTMLTextAreaElement | HTMLDivElement | null) => void
  onChange?: (text: string) => void
  onBlur?: (event: React.FocusEvent<HTMLTextAreaElement>) => void
  isScrollMode?: boolean
  maxHeight?: number
  onOverflowChange?: (isOverflowing: boolean) => void
  onScrollPositionChange?: (
    scrollTop: number,
    maxScroll: number,
    isAtTop: boolean,
  ) => void
}

const AIEditableText: React.FC<AIEditableTextProps> = ({
  text,
  isLoading = false,
  style,
  animateType = 'NONE',
  isEditable: isEditableFromProps = false,
  onAnimatingChange,
  onRef,
  onChange,
  onBlur,
  isScrollMode = false,
  maxHeight = 250,
  onOverflowChange,
  onScrollPositionChange,
}) => {
  const [displayedText, setDisplayedText] = useState<string>('')
  const [isAnimating, setIsAnimating] = useState<boolean>(false)
  const [isEditable, setIsEditable] = useState<boolean>(false) // Initialize as non-editable
  const [isVisible, setIsVisible] = useState<boolean>(false)
  const [hasBeenVisible, setHasBeenVisible] = useState<boolean>(false) // Track if component has ever been visible
  const previousTextRef = useRef<string>('')
  const animationTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const currentTokensRef = useRef<string[]>([])
  const targetTokensRef = useRef<string[]>([])
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const hasSetInitialText = useRef<boolean>(false)
  const observerRef = useRef<IntersectionObserver | null>(null)
  const isAdjustingHeightRef = useRef<boolean>(false)
  const [isFocused, setIsFocused] = useState<boolean>(false)
  const [isAtTop, setIsAtTop] = useState<boolean>(false)
  const markdownContainerRef = useRef<HTMLDivElement>(null)
  const [, setScrollPosition] = useState<number>(0)
  const [scrollPositionRatio, setScrollPositionRatio] = useState<number>(0)

  const splitIntoTokens = (text: string): string[] => {
    if (!text) return []
    let tokens = text.split(' ')

    if (tokens.length === 1) {
      tokens = text.match(new RegExp(`.{1,${CHUNK_SIZE}}`, 'g')) || []
    }

    return tokens
  }

  const checkOverflow = useCallback(() => {
    if (!textareaRef.current) return

    const { scrollHeight, clientHeight } = textareaRef.current
    const isOverflowing = scrollHeight > clientHeight

    // Notify parent of overflow state
    onOverflowChange?.(isOverflowing)
  }, [onOverflowChange])

  const adjustTextareaHeight = useCallback(() => {
    // Prevent reentrancy - if we're already adjusting, don't start again
    if (isAdjustingHeightRef.current || !textareaRef.current) return

    try {
      isAdjustingHeightRef.current = true
      const textarea = textareaRef.current

      // Check if textarea is currently visible
      const isVisible =
        textarea.offsetParent !== null &&
        window.getComputedStyle(textarea).display !== 'none'

      if (!isVisible && markdownContainerRef.current) {
        // If textarea is not visible, use the markdown container's height
        const markdownHeight = markdownContainerRef.current.offsetHeight

        // Apply the markdown height directly
        textarea.style.height = `${markdownHeight}px`

        // Also store desired height to avoid flickering on subsequent renders
        ;(textarea as any).dataset.desiredHeight = markdownHeight
      } else {
        // If previously set from markdown, use that height first
        if ((textarea as any).dataset.desiredHeight) {
          textarea.style.height = `${(textarea as any).dataset.desiredHeight}px`
          delete (textarea as any).dataset.desiredHeight
        }

        // Regular height adjustment
        // Remember current scroll position
        const scrollTop = textarea.scrollTop

        // Reset height temporarily
        textarea.style.height = 'auto'

        // Calculate new height, respecting maxHeight
        const desiredHeight = textarea.scrollHeight
        const maxAllowedHeight = maxHeight || Number.MAX_SAFE_INTEGER
        const newHeight = Math.min(desiredHeight, maxAllowedHeight)

        // Apply the constrained height
        textarea.style.height = `${newHeight}px`

        // Only restore scroll position if we're at maxHeight (scrollable)
        if (desiredHeight > maxAllowedHeight) {
          textarea.scrollTop = scrollTop
        }
      }

      // Check if content overflows after height adjustment
      checkOverflow()

      // Release the lock
      isAdjustingHeightRef.current = false
    } catch (error) {
      console.error('Error adjusting textarea height:', error)
      isAdjustingHeightRef.current = false
    }
  }, [checkOverflow, maxHeight])

  // Adjust height immediately after content changes
  useEffect(() => {
    adjustTextareaHeight()
  }, [displayedText, adjustTextareaHeight])

  // Adjust on window resize
  useEffect(() => {
    window.addEventListener('resize', adjustTextareaHeight)

    return () => window.removeEventListener('resize', adjustTextareaHeight)
  }, [adjustTextareaHeight])

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (!isAnimating && isEditable) {
      let newText = e.target.value

      // Check if a new line was added
      //The following code helps users who doesn't know markdown to add a new line by pressing enter (adding two spaces)
      const lastCharacterInput = newText.charAt(e.target.selectionStart - 1)

      if (lastCharacterInput === '\n') {
        // Find where the cursor is
        const cursorPosition = e.target.selectionStart

        // Find the last line break before the cursor
        let precedingNewlineIndex = newText.lastIndexOf(
          '\n',
          cursorPosition - 2,
        )

        precedingNewlineIndex =
          precedingNewlineIndex === -1 ? 0 : precedingNewlineIndex

        // Get the line just before the new line
        const previousLine = newText.substring(
          precedingNewlineIndex,
          cursorPosition - 1,
        )

        // If the line doesn't end with two spaces, add them
        if (previousLine.length > 0 && !previousLine.endsWith('  ')) {
          // Save current scroll position
          const scrollTop = e.target.scrollTop

          const beforeCursor = newText.substring(0, cursorPosition - 1)
          const afterCursor = newText.substring(cursorPosition)

          // Add two spaces before the newline
          newText = beforeCursor + '  \n' + afterCursor

          // Set the modified text
          setDisplayedText(newText)
          onChange?.(newText)

          // Clear "adjusting" flag to ensure we can adjust height
          isAdjustingHeightRef.current = false

          // Restore cursor position AND scroll position in one synchronous operation
          // This prevents the brief scroll jump
          const restorePositions = () => {
            if (textareaRef.current) {
              // First restore scroll position
              textareaRef.current.scrollTop = scrollTop

              // Then set cursor position
              textareaRef.current.selectionStart = cursorPosition + 2 // +2 for the added spaces
              textareaRef.current.selectionEnd = cursorPosition + 2

              // Ensure scroll is still at the right place after cursor positioning
              // Sometimes setting cursor can affect scroll
              textareaRef.current.scrollTop = scrollTop
            }
          }

          // Execute immediately and also in the next tick to ensure it works
          restorePositions()
          requestAnimationFrame(restorePositions)
          setTimeout(restorePositions, 0)

          return // Exit early since we've handled the change
        }
      }

      // Regular text change (no newline or already has proper spaces)
      setDisplayedText(newText)
      onChange?.(newText)

      // Clear "adjusting" flag to ensure we can adjust height
      isAdjustingHeightRef.current = false
    }
  }

  const animateText = () => {
    const currentLength = currentTokensRef.current.length
    const targetLength = targetTokensRef.current.length

    if (currentLength < targetLength) {
      const newText = targetTokensRef.current
        .slice(0, currentLength + 1)
        .join(' ')

      setDisplayedText(newText)
      currentTokensRef.current = targetTokensRef.current.slice(
        0,
        currentLength + 1,
      )
      animationTimeoutRef.current = setTimeout(animateText, ANIMATION_INTERVAL)
    } else {
      setIsAnimating(false)
    }
  }

  const isCompletelyNewText = (oldText: string, newText: string): boolean => {
    // Check if the new text is completely different from the old one
    // This helps distinguish between text additions and complete replacements
    return oldText !== newText && !newText.startsWith(oldText)
  }

  useEffect(() => {
    // Only set up intersection observer when animation depends on scrolling
    // and the component hasn't been visible yet
    if (animateType === 'ANIMATE_ON_ENTER_SCREEN' && !hasBeenVisible) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setIsVisible(true)
              setHasBeenVisible(true)

              // Disconnect observer after becoming visible once
              observer.disconnect()
            }
          })
        },
        {
          root: null,
          rootMargin: '-200px',
          threshold: 0.1,
        },
      )

      observerRef.current = observer

      setTimeout(() => {
        if (wrapperRef.current && !hasBeenVisible) {
          observer.observe(wrapperRef.current)
        }
      }, 100)

      return () => {
        if (observerRef.current) {
          observerRef.current.disconnect()
        }
      }
    } else if (
      animateType === 'ANIMATE_NOW' ||
      animateType === 'NONE' ||
      hasBeenVisible
    ) {
      // For immediate animation, no animation, or after being visible once,
      // consider always visible
      setIsVisible(true)
    }
  }, [animateType, hasBeenVisible])

  useEffect(() => {
    if (isLoading || !text || text.trim() === '') {
      setDisplayedText('')

      return
    }

    // If animation type is none, just show the text directly
    if (animateType === 'NONE') {
      setDisplayedText(text)

      return
    }

    // Only wait for visibility on initial render when using onScroll
    // Once we're visible or using immediate animation, proceed
    if (
      animateType === 'ANIMATE_ON_ENTER_SCREEN' &&
      !isVisible &&
      !hasBeenVisible
    ) {
      if (!hasSetInitialText.current) {
        setDisplayedText('') // Keep text empty until visible
      }

      return
    }

    // Should animate - either immediately or element is now visible for onScroll
    if (!hasSetInitialText.current) {
      hasSetInitialText.current = true // Mark that we're handling initial text
      currentTokensRef.current = []
      setIsAnimating(true)
      targetTokensRef.current = splitIntoTokens(text)
      animateText()

      return
    }

    // Handle subsequent text changes
    if (animationTimeoutRef.current) {
      clearTimeout(animationTimeoutRef.current)
    }

    if (isCompletelyNewText(previousTextRef.current, text)) {
      currentTokensRef.current = []
      setDisplayedText('')
      setIsAnimating(true)
      targetTokensRef.current = splitIntoTokens(text)
      animateText()
    } else if (!isAnimating) {
      currentTokensRef.current = splitIntoTokens(displayedText || '')
      setIsAnimating(true)
      targetTokensRef.current = splitIntoTokens(text)
      animateText()
    }

    previousTextRef.current = text

    return () => {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current)
      }
    }
    /* eslint-disable-next-line */
  }, [text, isLoading, isVisible, animateType, hasBeenVisible])

  // Reset hasSetInitialText when text changes completely
  useEffect(() => {
    // Only reset if text is completely different
    if (isCompletelyNewText(previousTextRef.current, text)) {
      hasSetInitialText.current = false
    }
  }, [text])

  // Add effect to sync with parent's editable state
  useEffect(() => {
    const wasEditable = isEditable

    setIsEditable(isEditableFromProps)

    // When switching to edit mode
    if (isEditableFromProps && !wasEditable) {
      // Reset the adjusting flag immediately
      isAdjustingHeightRef.current = false

      // Immediate adjustment attempt
      adjustTextareaHeight()

      // Sync the scroll position immediately for faster response
      if (textareaRef.current && markdownContainerRef.current) {
        const textarea = textareaRef.current
        const { clientHeight, scrollHeight } = textarea
        const maxScroll = scrollHeight - clientHeight

        if (maxScroll > 0) {
          const targetScroll = Math.min(
            scrollPositionRatio * maxScroll,
            maxScroll,
          )

          textarea.scrollTop = targetScroll
        }
      }

      setTimeout(() => {
        isAdjustingHeightRef.current = false
        adjustTextareaHeight()
      }, 10)
    }
    // When switching to view mode
    else if (!isEditableFromProps && wasEditable) {
      // Sync the scroll position immediately for faster response
      if (markdownContainerRef.current && textareaRef.current) {
        const container = markdownContainerRef.current
        const { clientHeight, scrollHeight } = container
        const maxScroll = scrollHeight - clientHeight

        if (maxScroll > 0) {
          const targetScroll = Math.min(
            scrollPositionRatio * maxScroll,
            maxScroll,
          )

          container.scrollTop = targetScroll
        }
      }
    }
    /* eslint-disable-next-line */
  }, [isEditableFromProps, adjustTextareaHeight, scrollPositionRatio])

  // Add effect to notify parent of animation state changes
  useEffect(() => {
    onAnimatingChange?.(isAnimating)
  }, [isAnimating, onAnimatingChange])

  // Remove the auto-focus effect
  // Instead, expose the textarea ref to parent
  useEffect(() => {
    if (textareaRef.current && onRef) {
      onRef?.(textareaRef.current)
    }
    /* eslint-disable-next-line */
  }, [textareaRef.current, onRef])

  // Handle textarea scroll event to report position to parent
  const handleTextareaScroll = useCallback(() => {
    if (!textareaRef.current) return

    const { scrollTop, scrollHeight, clientHeight } = textareaRef.current
    const maxScroll = scrollHeight - clientHeight
    const isAtTop = scrollTop === 0

    setIsAtTop(isAtTop)

    // Store both absolute position and ratio for better sync between differently sized containers
    setScrollPosition(scrollTop)
    setScrollPositionRatio(maxScroll > 0 ? scrollTop / maxScroll : 0)

    // Notify parent of scroll position
    onScrollPositionChange?.(scrollTop, maxScroll, isAtTop)
  }, [onScrollPositionChange])

  // Check if content overflows and notify parent
  // Set up scroll event listener
  useEffect(() => {
    const textarea = textareaRef.current

    if (!textarea) return

    textarea.addEventListener('scroll', handleTextareaScroll)

    return () => textarea.removeEventListener('scroll', handleTextareaScroll)
  }, [handleTextareaScroll])

  // Check overflow when content changes
  useEffect(() => {
    if (!isLoading && displayedText) {
      checkOverflow()

      // Also check after slight delay to account for rendering
      const timeoutId = setTimeout(checkOverflow, 100)

      return () => clearTimeout(timeoutId)
    }
  }, [displayedText, isLoading, checkOverflow])

  // Add resize observer to monitor content size changes
  useEffect(() => {
    if (!textareaRef.current) return

    const resizeObserver = new ResizeObserver(() => {
      checkOverflow()
    })

    resizeObserver.observe(textareaRef.current)

    return () => resizeObserver.disconnect()
  }, [checkOverflow])

  // Handle scroll for the markdown container
  const handleMarkdownScroll = useCallback(() => {
    if (!markdownContainerRef.current || isEditable) return

    const container = markdownContainerRef.current
    const { scrollTop, scrollHeight, clientHeight } = container
    const maxScroll = scrollHeight - clientHeight
    const isAtTop = scrollTop === 0

    setIsAtTop(isAtTop)

    // Store both absolute position and ratio for better sync between differently sized containers
    setScrollPosition(scrollTop)
    setScrollPositionRatio(maxScroll > 0 ? scrollTop / maxScroll : 0)

    // Notify parent of scroll position
    onScrollPositionChange?.(scrollTop, maxScroll, isAtTop)
  }, [onScrollPositionChange, isEditable])

  // Set up scroll event listener for markdown container
  useEffect(() => {
    const container = markdownContainerRef.current

    if (!container || isEditable) return

    container.addEventListener('scroll', handleMarkdownScroll)

    return () => container.removeEventListener('scroll', handleMarkdownScroll)
  }, [handleMarkdownScroll, isEditable])

  // Check overflow for markdown container
  const checkMarkdownOverflow = useCallback(() => {
    if (!markdownContainerRef.current || isEditable) return

    const container = markdownContainerRef.current
    const isOverflowing = container.scrollHeight > container.clientHeight

    // Notify parent of overflow state
    onOverflowChange?.(isOverflowing)
  }, [onOverflowChange, isEditable])

  // Check overflow when content changes for markdown container
  useEffect(() => {
    if (!isLoading && displayedText && !isEditable) {
      checkMarkdownOverflow()

      // Also check after slight delay to account for rendering
      const timeoutId = setTimeout(checkMarkdownOverflow, 100)

      return () => clearTimeout(timeoutId)
    }
  }, [displayedText, isLoading, checkMarkdownOverflow, isEditable])

  // Expose whichever ref is currently active to parent
  useEffect(() => {
    if (!onRef) return

    if (isEditable && textareaRef.current) {
      onRef(textareaRef.current)
    } else if (!isEditable && markdownContainerRef.current) {
      onRef(markdownContainerRef.current)
    }
  }, [isEditable, onRef])

  // Synchronize scroll position when switching between edit and view modes - make this faster
  useEffect(() => {
    // Apply scroll immediately when DOM is ready
    const applyImmediateScroll = () => {
      if (!isEditable && markdownContainerRef.current) {
        const container = markdownContainerRef.current
        const { clientHeight, scrollHeight } = container
        const maxScroll = scrollHeight - clientHeight

        if (maxScroll > 0) {
          const targetScroll = Math.min(
            scrollPositionRatio * maxScroll,
            maxScroll,
          )

          container.scrollTop = targetScroll
        }
      } else if (isEditable && textareaRef.current) {
        const textarea = textareaRef.current
        const { clientHeight, scrollHeight } = textarea
        const maxScroll = scrollHeight - clientHeight

        if (maxScroll > 0) {
          const targetScroll = Math.min(
            scrollPositionRatio * maxScroll,
            maxScroll,
          )

          textarea.scrollTop = targetScroll
        }
      }
    }

    // Try to apply immediately
    applyImmediateScroll()

    // Also try with requestAnimationFrame for better timing with browser painting
    requestAnimationFrame(applyImmediateScroll)

    // Keep the small timeout as a fallback for complex DOM updates
    const syncTimeout = setTimeout(applyImmediateScroll, 10)

    return () => clearTimeout(syncTimeout)
  }, [isEditable, scrollPositionRatio])

  if (isLoading || !text || text.trim() === '') {
    return <LoadingText style={style} isLoading={true} />
  }

  return (
    <div
      ref={wrapperRef}
      style={{
        width: '100%',
        height: '100%',
        minHeight: '5rem',
        display: 'block',
        position: 'relative',
      }}
    >
      {/* Keep both elements in the DOM always, but only show the active one */}
      <textarea
        ref={textareaRef}
        value={displayedText}
        onChange={handleChange}
        onScroll={handleTextareaScroll}
        onBlur={(e) => {
          setIsFocused(false)
          onBlur?.(e)
        }}
        onFocus={() => setIsFocused(true)}
        css={textareaStyle({
          isEditable,
          isFocused,
          isScrollMode: isScrollMode || false,
          isScrolledFromTop: !isAtTop,
        })}
        style={{
          ...style,
          cursor: 'text',
          overflowY: 'auto',
          maxHeight: maxHeight || 'none',
          display: isEditable ? 'block' : 'none', // Hide when not editable
        }}
      />
      <div
        ref={markdownContainerRef}
        css={markdownContainerStyle({
          isScrollMode: isScrollMode || false,
          isScrolledFromTop: !isAtTop,
        })}
        style={{
          ...style,
          cursor: 'default',
          overflowY: isScrollMode ? 'auto' : 'hidden',
          maxHeight: maxHeight || 'none',
          width: '100%',
          display: isEditable ? 'none' : 'block', // Hide when editable
        }}
      >
        <ReactMarkdown>{displayedText}</ReactMarkdown>
      </div>
    </div>
  )
}

export default AIEditableText
